import type { KeyboardEvent } from "react";
import { type Descendant, Editor, Element as SlateElement, Transforms } from "slate";
import type { CustomText, ParagraphElement } from "@/interfaces/CampaignBuilder";
import type { TextAlign, TextFormat } from "@/types/types";

// ── HTML serializer / deserializer ─────────────────────────────────────────

export function serializeHtml(nodes: Descendant[]): string {
  return nodes
    .map((node) => {
      if (!SlateElement.isElement(node)) return "";
      const el = node as ParagraphElement;
      const styleAttr = el.align && el.align !== "left" ? ` style="text-align: ${el.align};"` : "";
      const leaves = node.children as CustomText[];
      // A textless paragraph is a blank line the user typed. Emitted bare, `<p></p>` collapses to
      // zero height everywhere except inside contenteditable (Tailwind Preflight zeroes every `p`
      // margin), so the gap visible in the editor disappeared from the preview and the sent email.
      // `<br>` is the standard rich-text filler: it gives the line a box in every renderer.
      if (!leaves.some((leaf) => leaf.text.length > 0)) {
        return `<p${styleAttr}><br></p>`;
      }
      const children = leaves
        .map((leaf) => {
          let text = leaf.text
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/\n/g, "<br>");
          if (leaf.bold) text = `<strong>${text}</strong>`;
          if (leaf.italic) text = `<em>${text}</em>`;
          if (leaf.underline) text = `<u>${text}</u>`;
          return text;
        })
        .join("");
      return `<p${styleAttr}>${children}</p>`;
    })
    .join("");
}

function parseInlineHtml(inner: string): CustomText[] {
  const leaves: CustomText[] = [];
  const tokens = inner.split(/(<[^>]+>)/g);
  const marks = { bold: false, italic: false, underline: false };
  let current = "";

  function flush() {
    if (!current) return;
    const text = current.replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">");
    const leaf: CustomText = { text };
    if (marks.bold) leaf.bold = true;
    if (marks.italic) leaf.italic = true;
    if (marks.underline) leaf.underline = true;
    leaves.push(leaf);
    current = "";
  }

  for (const token of tokens) {
    if (!token.startsWith("<")) {
      current += token;
      continue;
    }
    const tag = token.toLowerCase();
    if (/<br\s*\/?>/.test(tag)) {
      current += "\n";
    } else if (tag === "<strong>" || tag === "<b>") {
      flush();
      marks.bold = true;
    } else if (tag === "</strong>" || tag === "</b>") {
      flush();
      marks.bold = false;
    } else if (tag === "<em>" || tag === "<i>") {
      flush();
      marks.italic = true;
    } else if (tag === "</em>" || tag === "</i>") {
      flush();
      marks.italic = false;
    } else if (tag === "<u>") {
      flush();
      marks.underline = true;
    } else if (tag === "</u>") {
      flush();
      marks.underline = false;
    }
  }

  flush();
  return leaves.length > 0 ? leaves : [{ text: "" }];
}

export function deserializeHtml(html: string): Descendant[] {
  if (!html) {
    return [{ type: "paragraph", children: [{ text: "" }] }];
  }

  // Parse <p> tags as separate paragraphs
  if (/<p[\s>]/i.test(html)) {
    const paragraphs: Descendant[] = [];
    const pRegex = /<p[^>]*>([\s\S]*?)<\/p>/gi;
    let match: RegExpExecArray | null;
    // biome-ignore lint/suspicious/noAssignInExpressions: standard regex iteration
    while ((match = pRegex.exec(html)) !== null) {
      const pTag = match[0] ?? "";
      const innerRaw = (match[1] ?? "").trim();
      const alignMatch = pTag.match(/text-align:\s*(left|center|right)/i);
      const align = alignMatch ? (alignMatch[1]?.toLowerCase() as TextAlign) : undefined;
      // A `<p>` whose whole content is one `<br>` is the blank-line convention from
      // `serializeHtml` — the `<br>` is a line-box filler, not a soft break, so it must not become
      // a "\n" in the text (that would charge a phantom character to the user's limit and grow the
      // stored HTML on every save). A `<br>` sitting next to text stays a soft break.
      const inner = /^<br\s*\/?>$/i.test(innerRaw) ? "" : innerRaw;
      // Blank paragraphs are kept. Discarding them here used to destroy the user's line breaks
      // permanently on the next load, not just render them wrong.
      paragraphs.push({
        type: "paragraph" as const,
        children: parseInlineHtml(inner),
        ...(align ? { align } : {}),
      });
    }
    if (paragraphs.length > 0) return paragraphs;
  }

  // Fallback: strip tags to plain text
  const plainText = html
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!plainText) {
    return [{ type: "paragraph", children: [{ text: "" }] }];
  }

  return [{ type: "paragraph", children: [{ text: plainText }] }];
}

// ── Hotkey detection ──────────────────────────────────────────────────────

export function matchesHotkey(e: KeyboardEvent, hotkey: string): boolean {
  const isMod = e.ctrlKey || e.metaKey;
  const key = e.key.toLowerCase();
  const [modifier, hotkeyKey] = hotkey.split("+");
  return modifier === "mod" && isMod && key === hotkeyKey;
}

// ── Toolbar helpers ────────────────────────────────────────────────────────

export const HOTKEYS: Record<string, TextFormat> = {
  "mod+b": "bold",
  "mod+i": "italic",
  "mod+u": "underline",
};

export function isMarkActive(editor: Editor, format: TextFormat) {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
}

export function toggleMark(editor: Editor, format: TextFormat) {
  if (isMarkActive(editor, format)) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
}

export function isAlignActive(editor: Editor, align: TextAlign) {
  const [match] = Editor.nodes(editor, {
    match: (n) => {
      if (!SlateElement.isElement(n)) return false;
      const el = n as ParagraphElement;
      return (el.align ?? "left") === align;
    },
  });
  return !!match;
}

export function setAlign(editor: Editor, align: TextAlign) {
  Transforms.setNodes(
    editor,
    { align },
    { match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) },
  );
}

// ── Character counter ──────────────────────────────────────────────────────

export function getPlainLength(nodes: Descendant[]): number {
  return nodes
    .flatMap((n) => (SlateElement.isElement(n) ? n.children : []))
    .reduce((sum, leaf) => sum + ((leaf as CustomText).text?.length ?? 0), 0);
}
